import { AddPhotoAlternate, PhotoCamera } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { apiPost, fileSelector, ProductImageIdentificationResponse, UploadState } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { useUploadManager } from '../../libraries/UploadManager';
import { RootState } from '../../store';

export interface ProductImagePickerProps {
    disabled?: boolean;
    onIdentified: (response: ProductImageIdentificationResponse) => void;
}

type ProductImagePickerStage = 'processing' | 'feedback' | null;

const MAX_PRODUCT_IMAGE_EDGE = 2048;
const PRODUCT_IMAGE_JPEG_QUALITY = 0.82;
const SMALL_IMAGE_THRESHOLD_BYTES = 1_000_000;

interface DecodedImage {
    source: CanvasImageSource;
    width: number;
    height: number;
    release: () => void;
}

const decodeImage = async (file: File): Promise<DecodedImage> => {
    if ('createImageBitmap' in window) {
        try {
            const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
            return {
                source: bitmap,
                width: bitmap.width,
                height: bitmap.height,
                release: () => bitmap.close()
            };
        } catch (_error) {
            // Safari peut décoder certains formats de l’appareil photo avec Image, mais pas avec ImageBitmap.
        }
    }

    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const element = new window.Image();
            element.onload = () => resolve(element);
            element.onerror = () => reject(new Error(`Le format de ${file.name} ne peut pas être préparé`));
            element.src = objectUrl;
        });

        return {
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            release: () => URL.revokeObjectURL(objectUrl)
        };
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }
};

const canvasToJpeg = (canvas: HTMLCanvasElement): Promise<Blob> => new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error("L’image n’a pas pu être compressée"));
    }, 'image/jpeg', PRODUCT_IMAGE_JPEG_QUALITY);
});

const compressProductImage = async (file: File): Promise<File> => {
    const decoded = await decodeImage(file);

    try {
        const scale = Math.min(1, MAX_PRODUCT_IMAGE_EDGE / Math.max(decoded.width, decoded.height));
        if (scale === 1 && file.size <= SMALL_IMAGE_THRESHOLD_BYTES) return file;

        const width = Math.max(1, Math.round(decoded.width * scale));
        const height = Math.max(1, Math.round(decoded.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        if (!context) throw new Error("L’image n’a pas pu être préparée");

        context.fillStyle = '#fff';
        context.fillRect(0, 0, width, height);
        context.drawImage(decoded.source, 0, 0, width, height);

        const compressedBlob = await canvasToJpeg(canvas);
        const wasResized = scale < 1;
        if (!wasResized && compressedBlob.size >= file.size) return file;

        const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo-produit';
        return new File([compressedBlob], `${baseName}.jpg`, {
            type: 'image/jpeg',
            lastModified: file.lastModified
        });
    } finally {
        decoded.release();
    }
};

export const ProductImagePicker = (props: ProductImagePickerProps) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const cameraInputRef = React.useRef<HTMLInputElement>(null);
    const abortControllerRef = React.useRef<AbortController | null>(null);
    const requestGenerationRef = React.useRef(0);
    const identificationStartedRef = React.useRef(false);
    const [stage, setStage] = React.useState<ProductImagePickerStage>(null);
    const [message, setMessage] = React.useState('');
    const [uploadIds, setUploadIds] = React.useState<string[]>([]);
    const [imagesBeingPrepared, setImagesBeingPrepared] = React.useState(0);
    const uploads = useSelector((state: RootState) => state.uploadManager.files);
    const savedFiles = useSelector((state: RootState) => fileSelector.selectAll(state));
    const { queueFiles } = useUploadManager();

    const trackedUploads = React.useMemo(() => uploadIds
        .map(uploadId => uploads.find(upload => upload.id === uploadId))
        .filter(upload => !!upload), [uploadIds, uploads]);
    const uploadsAreSaved = !!uploadIds.length
        && trackedUploads.length === uploadIds.length
        && trackedUploads.every(upload => upload.state === UploadState.UPLOADED && !!upload.fileId);

    const reset = React.useCallback(() => {
        requestGenerationRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        identificationStartedRef.current = false;
        setUploadIds([]);
        setImagesBeingPrepared(0);
        setMessage('');
        setStage(null);
    }, []);

    const onFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.currentTarget.files ?? []);
        event.currentTarget.value = '';

        if (!files.length) return;

        const generation = requestGenerationRef.current;
        setImagesBeingPrepared(files.length);
        setMessage('');
        setStage('processing');

        const preparedFiles: File[] = [];
        for (const file of files) {
            if (generation !== requestGenerationRef.current) return;

            try {
                preparedFiles.push(await compressProductImage(file));
            } catch (error) {
                console.warn('Product image compression failed; uploading the original file', error);
                preparedFiles.push(file);
            }
        }

        if (generation !== requestGenerationRef.current) return;

        const newUploadIds = queueFiles(preparedFiles, {
            accept: ['image/*'],
            multiple: true
        });
        setImagesBeingPrepared(0);

        if (!newUploadIds.length) {
            setMessage('Les fichiers sélectionnés ne sont pas des images reconnues.');
            setStage('feedback');
            return;
        }

        identificationStartedRef.current = false;
        setUploadIds(currentUploadIds => [...currentUploadIds, ...newUploadIds]);
        setMessage('');
        setStage('processing');
    };

    React.useEffect(() => {
        if (stage !== 'processing' || !uploadIds.length || identificationStartedRef.current) return;

        const failedUploads = trackedUploads.filter(upload => upload.state === UploadState.ERROR);
        if (failedUploads.length) {
            identificationStartedRef.current = true;
            const failedIds = new Set(failedUploads.map(upload => upload.id));
            setUploadIds(currentUploadIds => currentUploadIds.filter(uploadId => !failedIds.has(uploadId)));
            setMessage(failedUploads[0].error || "Une image n'a pas pu être téléversée. Vous pouvez en ajouter une autre et réessayer.");
            setStage('feedback');
            return;
        }

        if (!uploadsAreSaved) return;

        const files = trackedUploads
            .map(upload => savedFiles.find(file => file.id === upload.fileId))
            .filter(file => !!file);
        if (files.length !== uploadIds.length) return;

        identificationStartedRef.current = true;
        const generation = requestGenerationRef.current;
        const controller = new AbortController();
        abortControllerRef.current = controller;

        void apiPost('productImageIdentification', files, { signal: controller.signal })
            .then(response => {
                if (generation !== requestGenerationRef.current) return;

                if (response.results.length) {
                    reset();
                    props.onIdentified(response);
                } else {
                    setMessage(response.message);
                    setStage('feedback');
                }
            })
            .catch(error => {
                if (generation !== requestGenerationRef.current || controller.signal.aborted) return;
                console.error('Product image identification failed', error);
                setMessage("La demande d'identification n'a pas pu être complétée. Vous pouvez ajouter des images ou nous en aviser.");
                setStage('feedback');
            })
            .finally(() => {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
            });
    }, [props, reset, savedFiles, stage, trackedUploads, uploadIds, uploadsAreSaved]);

    return <>
        <input
            ref={inputRef}
            hidden
            type="file"
            accept="image/*"
            multiple
            onChange={onFilesSelected}
        />
        <input
            ref={cameraInputRef}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFilesSelected}
        />
        <Button
            type="button"
            variant="outlined"
            disabled={props.disabled}
            aria-label="Demander à ChatGPT à partir de photos"
            title="Demander à ChatGPT"
            onClick={() => cameraInputRef.current?.click()}
            sx={{ minWidth: 56, px: 1.5 }}
        >
            <PhotoCamera />
        </Button>
        <Dialog
            open={stage !== null}
            onClose={reset}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {stage === 'processing' ? 'Traitement en cours' : "Information sur l'identification"}
            </DialogTitle>
            <DialogContent dividers>
                {stage === 'processing' ? <Stack alignItems="center" spacing={2.5} sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography align="center">
                        {imagesBeingPrepared
                            ? `Optimisation de ${imagesBeingPrepared} image${imagesBeingPrepared > 1 ? 's' : ''}…`
                            : uploadsAreSaved
                            ? 'Analyse des images et recherche du produit…'
                            : `Téléversement de ${uploadIds.length} image${uploadIds.length > 1 ? 's' : ''}…`}
                    </Typography>
                </Stack> : <Typography sx={{ py: 2 }}>
                    {message}
                </Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
                {stage === 'feedback' && <Button
                    type="button"
                    variant="outlined"
                    startIcon={<AddPhotoAlternate />}
                    onClick={() => inputRef.current?.click()}
                >
                    Ajouter des images
                </Button>}
                <Box sx={{ flex: 1 }} />
                <Button type="button" color="inherit" onClick={reset}>
                    Annuler la demande
                </Button>
            </DialogActions>
        </Dialog>
    </>;
};
